import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Compass, Lock, FileText, Star, MapPin, Clock, Smartphone, Users, Quote, ChevronRight, Download, X, Play, ChevronDown, ChevronUp, Navigation, Sparkles } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import AuthModal from '../components/AuthModal';
import HomepagePromoStrip from '../components/HomepagePromoStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Approximate "1.5 lines on mobile" cap — quotes longer than this collapse
// behind a Read more toggle so the testimonials grid stays scannable.
const TESTIMONIAL_PREVIEW_CHARS = 110;

// Star rating component
const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

// Collapsible testimonial card — long quotes collapse to ~1.5 lines with a Read more toggle.
const TestimonialCard = ({ item, index }) => {
  const [expanded, setExpanded] = useState(false);
  const quote = item.quote || '';
  const isLong = quote.length > TESTIMONIAL_PREVIEW_CHARS;
  const preview = isLong && !expanded
    ? `${quote.slice(0, TESTIMONIAL_PREVIEW_CHARS).trimEnd()}…`
    : quote;
  return (
    <div className="bg-white rounded-2xl p-6 shadow-xl flex flex-col" data-testid={`testimonial-card-${index}`}>
      <Quote className="w-10 h-10 text-cyan-500/30 mb-4 flex-shrink-0" />
      <p className="text-gray-700 italic" data-testid={`testimonial-quote-${index}`}>
        &ldquo;{preview}&rdquo;
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`testimonial-toggle-${index}`}
          className="mt-2 inline-flex items-center gap-1 text-cyan-600 hover:text-cyan-700 font-bold text-sm self-start transition-colors active:scale-95"
          aria-expanded={expanded}
        >
          {expanded ? (
            <>Read less <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Read more <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        <div>
          <p className="font-bold text-gray-900">{item.name}</p>
          {item.location && <p className="text-sm text-gray-500">{item.location}</p>}
          {item.tourTitle && <p className="text-xs text-cyan-600 mt-1">{item.tourTitle}</p>}
        </div>
        <StarRating rating={item.rating} />
      </div>
    </div>
  );
};

// PWA Install Banner Component
const InstallBanner = ({ onClose }) => {
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    
    // Detect device
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
    setIsMobile(/iphone|ipad|ipod|android|mobile/.test(userAgent));
  }, []);

  // Don't show if already installed or on desktop (walking tours are a mobile experience)
  if (isStandalone || !isMobile) return null;

  return (
    <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-4 px-4 relative">
      <button 
        onClick={onClose}
        className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Download className="w-6 h-6" />
          <h3 className="font-bold text-lg">Save Audiostride to Your Phone</h3>
        </div>
        <p className="text-white/90 text-sm mb-3">
          Add this page to your home screen for easy access - no app store needed!
        </p>
        
        {isIOS && (
          <div className="bg-white/20 rounded-lg p-3 text-sm">
            <p className="font-semibold mb-2">📱 Quick steps:</p>
            <ol className="list-decimal list-inside space-y-1 text-white/90">
              <li>
                Tap the <strong>&quot;…&quot;</strong> button at the bottom-right of Safari
                <span className="text-white/70"> (or the <strong>Share button</strong>{' '}
                <span className="inline-block bg-white/30 rounded px-1">↑</span> if you see it
                directly in the toolbar)</span>
              </li>
              <li>Tap <strong>&quot;Share&quot;</strong> in the menu that opens</li>
              <li>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></li>
              <li>Tap <strong>&quot;Add&quot;</strong> — done!</li>
            </ol>
          </div>
        )}
        
        {isAndroid && (
          <div className="bg-white/20 rounded-lg p-3 text-sm">
            <p className="font-semibold mb-2">📱 Quick steps:</p>
            <ol className="list-decimal list-inside space-y-1 text-white/90">
              <li>Tap the <strong>3 dots menu</strong> <span className="inline-block bg-white/30 rounded px-1">⋮</span> in the top right of Chrome</li>
              <li>Tap <strong>&quot;Add to Home screen&quot;</strong> or <strong>&quot;Install app&quot;</strong></li>
              <li>Tap <strong>&quot;Add&quot;</strong> — done!</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [tours, setTours] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalTours: 0, totalReviews: 0, avgRating: 0 });
  const [testimonials, setTestimonials] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [pinnedHero, setPinnedHero] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [resumeTour, setResumeTour] = useState(null);
  // Map of city → Set of tour_ids the current user has already completed
  // there. Powers the "You've done N of M {city} tours" nudge on tour cards
  // and is only populated for logged-in users (fetched from /passport/me).
  const [completedTourIdsByCity, setCompletedTourIdsByCity] = useState({});
  // User's current location (optional — sorted-by-proximity feature only).
  // Null until the browser resolves the location or the user denies it.
  const [userLocation, setUserLocation] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTours();
    fetchTestimonialsAndReviews();
    
    // Referral code capture now handled globally by useReferralCapture in
    // App.js (mounted inside BrowserRouter). No legacy write here.

    // Check if user has dismissed the install banner before
    const dismissed = localStorage.getItem('installBannerDismissed');
    if (!dismissed) {
      // Show install banner after a short delay
      setTimeout(() => setShowInstallBanner(true), 2000);
    }
    
    // Check for a tour the user paused/left mid-walk (last 7 days)
    if (token) {
      try {
        const raw = localStorage.getItem('audiostride_last_tour');
        if (raw) {
          const parsed = JSON.parse(raw);
          const ageMs = Date.now() - (parsed.last_seen || 0);
          const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
          if (ageMs < sevenDaysMs && parsed.purchase_id && parsed.current_stop < parsed.total_stops - 1) {
            setResumeTour(parsed);
          } else {
            localStorage.removeItem('audiostride_last_tour');
            setResumeTour(null);
          }
        }
      } catch (_) { /* malformed payload */ }
    } else {
      // Logged-out users shouldn't see the resume banner
      setResumeTour(null);
    }

    // One-shot, low-intrusion location request to sort tours by proximity.
    // We intentionally use getCurrentPosition (not watchPosition) so we don't
    // sit on the GPS battery, and we silently fall back to the default order
    // if the user denies, dismisses, or has no GPS.
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => { /* denied or unavailable — keep default tour order */ },
        { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 }
      );
    }
  }, [token]);

  // Fetch which tours the logged-in user has already completed per city, so
  // we can highlight "You've done 1 of 3 Tel Aviv tours" on tours they haven't
  // walked yet — turning collector momentum into a soft cross-sell. Silent
  // failure — the chip just doesn't show if the request errors.
  useEffect(() => {
    if (!token) {
      setCompletedTourIdsByCity({});
      return;
    }
    let ignore = false;
    axios.get(`${API}/passport/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (ignore) return;
      const map = {};
      for (const row of (res.data?.cities_walked || [])) {
        if (row?.city && Array.isArray(row.tour_ids)) {
          map[row.city] = new Set(row.tour_ids);
        }
      }
      setCompletedTourIdsByCity(map);
    }).catch(() => { /* silent — no chip is a fine fallback */ });
    return () => { ignore = true; };
  }, [token]);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API}/tours`);
      const toursData = response.data;
      setTours(toursData);
      
      // Calculate stats
      const totalReviews = toursData.reduce((sum, t) => sum + (t.review_count || 0), 0);
      const toursWithRatings = toursData.filter(t => t.average_rating > 0);
      const avgRating = toursWithRatings.length > 0 
        ? toursWithRatings.reduce((sum, t) => sum + t.average_rating, 0) / toursWithRatings.length
        : 0;
      setStats({
        totalTours: toursData.length,
        totalReviews,
        avgRating: avgRating
      });
    } catch (error) {
      console.error('Failed to load tours:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonialsAndReviews = async () => {
    try {
      // Fetch admin-managed testimonials
      const testimonialsRes = await axios.get(`${API}/testimonials`);
      setTestimonials(testimonialsRes.data || []);

      
      // Fetch featured real reviews
      const reviewsRes = await axios.get(`${API}/reviews/featured?limit=6`);
      setFeaturedReviews(reviewsRes.data || []);

      // Fetch the admin-pinned hero item (either a testimonial OR a review).
      // Single source of truth — frontend doesn't need to merge & search.
      try {
        const heroRes = await axios.get(`${API}/hero-highlight`);
        setPinnedHero(heroRes.data || null);
      } catch (_) {
        setPinnedHero(null);
      }
    } catch (error) {
      console.error('Failed to load testimonials/reviews:', error);
    }
  };

  const handleTourClick = (tourId) => {
    navigate(`/tours/${tourId}`);
  };

  const handleDismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('installBannerDismissed', 'true');
  };

  // Combine admin testimonials and real reviews for display.
  // Dedup guard: a testimonial promoted from a review carries source_review_id.
  // When we also fetch /reviews/featured, the source review will appear again —
  // skip those so each person only shows up once.
  const promotedReviewIds = new Set(
    testimonials
      .map(t => t.source_review_id)
      .filter(Boolean)
  );
  // Secondary guard: in case the testimonial wasn't promoted via the API path
  // (e.g. legacy/manual entries), also dedupe by normalized quote text.
  const seenQuotes = new Set(
    testimonials.map(t => (t.quote || '').replace(/\s+/g, ' ').trim().toLowerCase())
  );
  const allTestimonials = [
    ...testimonials.map(t => ({
      name: t.name,
      location: t.location,
      quote: t.quote,
      rating: t.rating,
      type: 'testimonial'
    })),
    ...featuredReviews
      .filter(r => !promotedReviewIds.has(r.review_id))
      .filter(r => !seenQuotes.has((r.comment || '').replace(/\s+/g, ' ').trim().toLowerCase()))
      .map(r => ({
        name: r.user_name,
        location: r.user_city || r.tour_city || '',
        quote: r.comment,
        rating: r.rating,
        type: 'review',
        tourTitle: r.tour_title
      }))
  ].slice(0, 6); // Max 6 testimonials

  // Pick the single strongest review for the hero social-proof strip.
  // 1. Prefer the admin-pinned item (testimonial OR user review) returned by
  //    the dedicated /hero-highlight endpoint — single source of truth.
  // 2. Otherwise auto-pick: highest rating, then longest meaningful quote.
  const heroTestimonial = (() => {
    if (pinnedHero && (pinnedHero.quote || '').trim()) {
      return {
        name: pinnedHero.name,
        location: pinnedHero.location,
        quote: pinnedHero.quote,
        rating: pinnedHero.rating,
        tourTitle: pinnedHero.tour_title || '',
        type: pinnedHero.type,
        pinned: true,
      };
    }
    const ranked = [...allTestimonials]
      .filter((t) => (t.quote || '').trim().length >= 20)
      .sort((a, b) => {
        const ra = a.rating || 0;
        const rb = b.rating || 0;
        if (rb !== ra) return rb - ra;
        return (b.quote || '').length - (a.quote || '').length;
      });
    return ranked[0] || null;
  })();

  // Sort tours by proximity to user. If location isn't available (denied,
  // unsupported, still resolving), fall back to the original order from the
  // server. Tours without geocoded stops sink to the bottom of the proximity
  // list so they still appear, just not first.
  const sortedTours = (() => {
    if (!userLocation) return tours;
    const R = 6371; // km
    const toRad = (deg) => (deg * Math.PI) / 180;
    const distKm = (lat, lng) => {
      const dLat = toRad(lat - userLocation.lat);
      const dLng = toRad(lng - userLocation.lng);
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(userLocation.lat)) * Math.cos(toRad(lat)) *
        Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };
    return [...tours]
      .map((t) => {
        const lat = t.representative_lat;
        const lng = t.representative_lng;
        const d = (typeof lat === 'number' && typeof lng === 'number') ? distKm(lat, lng) : null;
        return { ...t, _distance_km: d };
      })
      .sort((a, b) => {
        if (a._distance_km === null && b._distance_km === null) return 0;
        if (a._distance_km === null) return 1;
        if (b._distance_km === null) return -1;
        return a._distance_km - b._distance_km;
      });
  })();

  // Total published tours per city — powers the "N of M {city} tours"
  // catalog-completion chip on tour cards. Computed once per render from
  // the loaded tours list; O(n).
  const toursPerCity = React.useMemo(() => {
    const map = {};
    for (const t of tours || []) {
      const city = t?.city;
      if (city) map[city] = (map[city] || 0) + 1;
    }
    return map;
  }, [tours]);

  // "Almost there!" cities — the traveller has completed all but exactly one
  // tour in the city's catalog. Shown as a soft amber banner above the tour
  // list with a click-to-scroll to the missing tour. Turns the passive chip
  // into an active CTA. Only computed once per render.
  const almostThereCities = React.useMemo(() => {
    const rows = [];
    for (const city of Object.keys(toursPerCity)) {
      const total = toursPerCity[city] || 0;
      const doneSet = completedTourIdsByCity[city];
      const done = doneSet ? doneSet.size : 0;
      if (total >= 2 && done === total - 1) {
        // Find the single remaining tour — first tour in this city NOT in
        // the completed set. We use the sorted tour list so the "closest to
        // you" missing tour surfaces first, matching the rest of the page.
        const missing = (tours || []).find(
          (t) => t.city === city && !doneSet.has(t.tour_id)
        );
        if (missing) {
          rows.push({ city, missing_tour_id: missing.tour_id, total });
        }
      }
    }
    return rows;
  }, [toursPerCity, completedTourIdsByCity, tours]);

  // Scroll to a specific tour card + briefly highlight it. Used by the
  // "Almost there!" banner CTA. Adds a temporary amber ring class so users
  // can visually track where the banner sent them.
  const scrollToTour = React.useCallback((tourId) => {
    const el = document.querySelector(`[data-testid="tour-card-${tourId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-4');
    setTimeout(() => {
      el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-4');
    }, 2400);
  }, []);

  // Threshold beyond which "Get directions" is hidden — a same-day reach
  // realistically tops out around 500 km / 300 mi. Anything further is a
  // flight, and turn-by-turn directions are not useful.
  const NAVIGATION_THRESHOLD_KM = 500;

  // Build a universal "open in maps app" link. iOS opens Apple Maps, Android
  // opens Google Maps, desktop opens Google Maps in a tab — all from the
  // same URL via the Google Maps universal dir API.
  const buildDirectionsUrl = (lat, lng) =>
    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  // Uses miles for users in countries that use imperial road distances
  // (US, UK, Liberia, Myanmar), kilometers everywhere else. The check is
  // best-effort via navigator.language; if undetectable we default to km.
  const usesMiles = (() => {
    try {
      const lang = (navigator.language || '').toLowerCase();
      const langs = (navigator.languages || []).map((l) => l.toLowerCase());
      const all = [lang, ...langs];
      // Match region codes -US, -GB, -LR, -MM (case-insensitive)
      return all.some((l) => /-(us|gb|lr|mm)\b/.test(l));
    } catch (_) {
      return false;
    }
  })();

  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (usesMiles) {
      const miles = km * 0.621371;
      if (miles < 0.1) return `${Math.round(miles * 5280)} ft away`;
      if (miles < 10) return `${miles.toFixed(1)} mi away`;
      if (miles < 1000) return `${Math.round(miles)} mi away`;
      return `${Math.round(miles).toLocaleString()} mi away`;
    }
    if (km < 1) return `${Math.round(km * 1000)} m away`;
    if (km < 10) return `${km.toFixed(1)} km away`;
    if (km < 1000) return `${Math.round(km)} km away`;
    return `${Math.round(km).toLocaleString()} km away`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Featured Promo Strip — pinned to the absolute top of the page */}
      <HomepagePromoStrip
        tours={tours}
        onCTA={(tour) =>
          navigate(`/tours/${tour.tour_id}?promo=${tour.promo_banner.code}`)
        }
      />

      {/* Install Banner */}
      {showInstallBanner && <InstallBanner onClose={handleDismissInstallBanner} />}
      
      {/* Resume Tour banner — only when the user has an unfinished walk in the last 7 days */}
      {resumeTour && (
        <button
          type="button"
          onClick={() => navigate(`/tour-player/${resumeTour.purchase_id}`)}
          data-testid="homepage-resume-banner"
          className="w-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white py-3 px-4 flex items-center justify-between gap-3 shadow-md active:scale-[0.99] transition-transform text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <Play className="w-5 h-5 fill-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide font-bold opacity-90">Continue where you left off</p>
              <p className="font-black text-base sm:text-lg truncate">
                {resumeTour.tour_title} · Stop {resumeTour.current_stop + 1}/{resumeTour.total_stops}
              </p>
            </div>
          </div>
          <span className="text-sm font-bold whitespace-nowrap pl-2">Resume →</span>
        </button>
      )}
      
      {/* Top Nav Bar for non-logged-in users */}
      {!token && (
        <nav className="bg-cyan-600 text-white py-3 px-4">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <span className="text-sm font-medium flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              No app download needed - works in your browser!
            </span>
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-white hover:text-cyan-200 font-semibold transition-colors"
              data-testid="nav-blog-link"
            >
              <FileText className="w-4 h-4" />
              Blog
            </Link>
          </div>
        </nav>
      )}

      {/* Hero Section */}
      <header className="bg-cyan-500 text-white py-16 sm:py-20 px-2 sm:px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-6">
            {/* Logo Image with rounded corners */}
            <div className="bg-white rounded-3xl py-6 px-8 mb-4 inline-block">
              <img 
                src="/audiostride-logo.png" 
                alt="Audiostride" 
                className="mx-auto"
                style={{ maxHeight: '150px', maxWidth: '100%' }}
              />
            </div>
            
            {/* Social Proof Badge - only show if there are real reviews */}
            {stats.totalReviews > 0 && stats.avgRating > 0 && (
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <StarRating rating={Math.round(stats.avgRating)} />
                <span className="font-semibold">{stats.avgRating.toFixed(1)}</span>
                <span className="text-white/80">({stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'})</span>
              </div>
            )}
            
            <div className="w-24 h-1 bg-white mx-auto mb-6"></div>
          </div>
          
          <div className="bg-blue-600 rounded-3xl py-6 sm:py-12 px-3 sm:px-8 mb-8 inline-block mx-1">
            <h1 
              className="font-black uppercase tracking-tight"
              style={{ fontSize: 'clamp(1.25rem, 6vw, 4rem)' }}
            >
              GPS AUDIO TOURS
            </h1>
          </div>
          
          <p className="text-sm sm:text-2xl mb-8 max-w-3xl mx-auto font-light px-2">
            Explore cities with immersive audio guides that play automatically as you walk
          </p>
          
          {!token && (
            <button
              onClick={() => setShowAuthModal(true)}
              data-testid="header-login-button"
              className="mt-4 bg-white text-cyan-600 px-8 sm:px-12 py-4 sm:py-5 rounded-full font-bold text-lg sm:text-xl shadow-xl hover:shadow-2xl transition-all active:scale-95 uppercase tracking-wide"
            >
              Get Started
            </button>
          )}
        </div>
      </header>

      {/* Tour Catalog Section */}
      <main className="max-w-6xl mx-auto px-4 py-16" id="tours">
        {/* Hero social-proof strip — surfaces the strongest review where the
            buying decision happens (just above the tour cards). */}
        {heroTestimonial && (() => {
          const fullQuote = (heroTestimonial.quote || '').trim();
          const capped = fullQuote.length > 200 ? `${fullQuote.slice(0, 200).trimEnd()}…` : fullQuote;
          return (
            <div className="mb-10 bg-gradient-to-br from-cyan-50 via-sky-50 to-white border border-cyan-100 rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="hero-testimonial">
              <div className="flex items-start gap-3 sm:gap-4">
                <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-500/40 flex-shrink-0 mt-1" />
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-lg text-slate-800 font-medium italic leading-snug" data-testid="hero-testimonial-quote">
                    &ldquo;{capped}&rdquo;
                  </p>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <StarRating rating={heroTestimonial.rating} />
                    <div className="text-sm">
                      <span className="font-bold text-slate-900">{heroTestimonial.name}</span>
                      {heroTestimonial.location && (
                        <span className="text-slate-500"> · {heroTestimonial.location}</span>
                      )}
                    </div>
                    {heroTestimonial.tourTitle && (
                      <span className="text-xs text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded-full font-bold">
                        {heroTestimonial.tourTitle}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="mb-12">
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-gray-900 mb-2">Available Tours</h2>
          <div className="w-20 h-1 bg-cyan-500 mb-4"></div>
          <p className="text-gray-600 text-lg">Choose a tour and start exploring today</p>
        </div>

        {/* Almost there! banner — active CTA when the traveller is exactly
            one tour away from finishing a city's catalog. Clicking the pill
            scrolls to the missing tour with a temporary amber ring. */}
        {almostThereCities.length > 0 && (
          <div className="mb-8 space-y-3" data-testid="almost-there-banners">
            {almostThereCities.map((row) => (
              <button
                type="button"
                key={row.city}
                onClick={() => scrollToTour(row.missing_tour_id)}
                data-testid={`almost-there-banner-${row.city}`}
                className="w-full text-left group bg-gradient-to-r from-amber-100 via-amber-50 to-orange-50 border-2 border-amber-300 hover:border-amber-500 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 shadow ring-2 ring-white">
                  <Sparkles className="w-6 h-6 text-amber-950" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-amber-950 text-base sm:text-lg leading-tight">
                    Almost there — one more {row.city} tour to finish the collection.
                  </div>
                  <div className="text-xs sm:text-sm text-amber-800 mt-0.5">
                    You&apos;ve done {row.total - 1} of {row.total} tours in {row.city}. Tap to jump to the last one.
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-800 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

        {tours.length === 0 ? (
          <div className="text-center py-16">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-body">No tours available yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {userLocation && tours.length > 1 && (
              <p className="text-sm text-cyan-700 font-semibold mb-4 flex items-center gap-1" data-testid="proximity-sort-note">
                <MapPin className="w-4 h-4" />
                Sorted by distance from you · closest first
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedTours.map((tour) => {
                // Catalog-completion nudge: only for logged-in users who
                // have started to collect this city. Hide on the tour
                // they've already walked so it reads as a "come back for
                // more" prompt, not a smug reminder of what they did.
                const completedInCity = completedTourIdsByCity[tour.city];
                const doneCount = completedInCity ? completedInCity.size : 0;
                const totalInCity = toursPerCity[tour.city] || 0;
                const showCityProgressChip =
                  !!token &&
                  doneCount > 0 &&
                  totalInCity > 1 &&
                  doneCount < totalInCity &&
                  completedInCity &&
                  !completedInCity.has(tour.tour_id);
                return (
              <div
                key={tour.tour_id}
                onClick={() => handleTourClick(tour.tour_id)}
                data-testid={`tour-card-${tour.tour_id}`}
                className="bg-white border-4 border-cyan-500 rounded-2xl overflow-hidden hover:border-blue-600 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
              >
                <div className="relative h-56 bg-gradient-to-br from-cyan-400 to-blue-500 overflow-hidden">
                  {tour.cover_image_url && (
                    <img 
                      src={tour.cover_image_url} 
                      alt={tour.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  
                  {/* Price Badge — show FREE for is_free tours */}
                  <div className="absolute top-4 right-4 bg-white rounded-full px-4 py-2 shadow-lg">
                    {tour.is_free ? (
                      <span className="text-lg font-black text-emerald-600" data-testid={`free-badge-${tour.tour_id}`}>FREE</span>
                    ) : (
                      // Defensive fallback: some catalog records were observed
                      // to be missing `base_price` (e.g. malformed seed data).
                      // Coalesce to 0 so the whole discovery page doesn't
                      // crash the moment one bad tour lands.
                      <span className="text-2xl font-black text-cyan-600">${(tour.base_price ?? 0).toFixed(0)}</span>
                    )}
                  </div>

                  {/* Private Access ribbon — invitation-only tours that are still listed */}
                  {tour.is_private && (
                    <div
                      data-testid={`private-badge-${tour.tour_id}`}
                      className="absolute top-3 left-3 bg-slate-900 text-white font-black uppercase text-xs tracking-[0.18em] px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 ring-2 ring-white/40 z-10"
                    >
                      <Lock className="w-3 h-3" />
                      Private Access
                    </div>
                  )}
                  
                  {/* Featured Promo Ribbon (top-left banner) */}
                  {tour.promo_banner && tour.promo_banner.code && (
                    <div
                      data-testid={`promo-ribbon-${tour.tour_id}`}
                      className="absolute top-3 left-3 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white font-black uppercase text-sm tracking-wide px-4 py-2 rounded-full shadow-lg flex items-center gap-2 ring-2 ring-white/60 z-10"
                    >
                      <span className="text-base">🎁</span>
                      <span>
                        {tour.promo_banner.discount_type === 'free' || tour.promo_banner.discount_percent === 100
                          ? 'FREE'
                          : `${tour.promo_banner.discount_percent}% OFF`}{' '}
                        with code{' '}
                        <span className="font-mono">{tour.promo_banner.code}</span>
                      </span>
                    </div>
                  )}
                  
                  {/* Rating Badge */}
                  {(tour.review_count || 0) > 0 && typeof tour.average_rating === 'number' && (
                    <div className="absolute top-14 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-bold text-gray-900">{tour.average_rating.toFixed(1)}</span>
                      <span className="text-gray-500 text-sm">({tour.review_count})</span>
                    </div>
                  )}
                  
                  <div className="absolute inset-0 flex items-end p-6">
                    <div>
                      <h3 className="text-white text-2xl sm:text-3xl font-bold mb-1 uppercase">{tour.title}</h3>
                      <p className="text-cyan-50 text-lg font-semibold">{tour.city}</p>
                      {/* City-progress nudge — only on tours the user hasn't
                          walked in a city they've already sampled. Amber pill
                          contrasts nicely with the cyan card border and
                          matches the passport palette. */}
                      {showCityProgressChip && (
                        <div
                          data-testid={`city-progress-chip-${tour.tour_id}`}
                          className="mt-2 inline-flex items-center gap-1.5 bg-amber-400/95 text-amber-950 rounded-full px-3 py-1 text-xs font-black uppercase tracking-wide shadow-md ring-1 ring-white/40"
                        >
                          <Sparkles className="w-3 h-3" />
                          You&apos;ve done {doneCount} of {totalInCity} {tour.city} tours
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-6 bg-gray-50">
                  {userLocation && typeof tour._distance_km === 'number' && (
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        data-testid={`tour-distance-${tour.tour_id}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1"
                      >
                        <MapPin className="w-3 h-3" />
                        {formatDistance(tour._distance_km)}
                      </span>
                      {tour._distance_km <= NAVIGATION_THRESHOLD_KM && (
                        <a
                          href={buildDirectionsUrl(tour.representative_lat, tour.representative_lng)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`tour-directions-${tour.tour_id}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100 transition-colors active:scale-95"
                        >
                          <Navigation className="w-3 h-3" />
                          Get directions
                        </a>
                      )}
                    </div>
                  )}
                  <p className="text-gray-700 text-base mb-4 line-clamp-2">{tour.description}</p>
                  
                  {/* Tour Details */}
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {tour.stop_count || 0} stops
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {tour.duration}
                    </span>
                  </div>

                  {/* Starting Point(s) — clearly show WHERE the tour begins so
                      travelers can plan how to get to the first stop. When
                      Express and Full share the same first stop we show just
                      one row; otherwise we show both. */}
                  {tour.starting_stop_full && (() => {
                    const fullStart = tour.starting_stop_full;
                    const exStart = tour.starting_stop_express;
                    const hasSeparateExpress = exStart && exStart.title && exStart.title !== fullStart.title;
                    return (
                      <div className="mb-4 rounded-xl bg-white border border-cyan-200 divide-y divide-cyan-100 overflow-hidden" data-testid={`tour-start-${tour.tour_id}`}>
                        <div className="px-3 py-2.5 flex items-center gap-2">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center">
                            <MapPin className="w-3.5 h-3.5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">
                              {hasSeparateExpress ? 'Full tour starts at' : 'Starts at'}
                            </p>
                            <p className="text-sm font-semibold text-slate-800 truncate" data-testid={`tour-start-full-title-${tour.tour_id}`}>
                              {fullStart.title}
                            </p>
                          </div>
                          <a
                            href={buildDirectionsUrl(fullStart.lat, fullStart.lng)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`tour-start-full-dir-${tour.tour_id}`}
                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-cyan-700 hover:text-cyan-900 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-full px-2.5 py-1 transition-colors"
                          >
                            <Navigation className="w-3 h-3" />
                            Directions
                          </a>
                        </div>
                        {hasSeparateExpress && (
                          <div className="px-3 py-2.5 flex items-center gap-2 bg-amber-50/70">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs">
                              ⚡
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                                Express starts at
                              </p>
                              <p className="text-sm font-semibold text-slate-800 truncate" data-testid={`tour-start-express-title-${tour.tour_id}`}>
                                {exStart.title}
                              </p>
                            </div>
                            <a
                              href={buildDirectionsUrl(exStart.lat, exStart.lng)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`tour-start-express-dir-${tour.tour_id}`}
                              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 hover:text-amber-900 bg-white hover:bg-amber-100 border border-amber-200 rounded-full px-2.5 py-1 transition-colors"
                            >
                              <Navigation className="w-3 h-3" />
                              Directions
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* CTA Button */}
                  <button className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-3 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 group-hover:bg-blue-600">
                    View Tour Details
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  {/* Express Option Note */}
                  {tour.express_enabled && tour.express_stop_count > 0 && (
                    <p className="text-amber-700 text-sm mt-3 text-center font-medium">
                      ⚡ Express option available ({tour.express_stop_count} highlights)
                    </p>
                  )}
                </div>
              </div>
              );
            })}
            </div>
          </>
        )}
      </main>

      {/* Testimonials Section - Only show if there are testimonials or reviews */}
      {allTestimonials.length > 0 && (
        <section className="bg-gradient-to-br from-cyan-500 to-blue-600 py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-black uppercase text-center text-white mb-4">What Travelers Say</h2>
            <p className="text-center text-white/80 mb-12 text-lg">Real experiences from our explorers</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="testimonials-grid">
              {allTestimonials.map((item, index) => (
                <TestimonialCard key={index} item={item} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Value Proposition Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black uppercase text-center text-gray-900 mb-12">Why Choose Audiostride?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">🎧</div>
              <h3 className="font-bold text-gray-900 mb-2">Professional Audio</h3>
              <p className="text-gray-600 text-sm">High-quality narration by local experts</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">📍</div>
              <h3 className="font-bold text-gray-900 mb-2">GPS-Triggered</h3>
              <p className="text-gray-600 text-sm">Audio plays automatically at each stop</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">⏰</div>
              <h3 className="font-bold text-gray-900 mb-2">Your Own Pace</h3>
              <p className="text-gray-600 text-sm">No schedules, no groups - explore freely</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">💰</div>
              <h3 className="font-bold text-gray-900 mb-2">Great Value</h3>
              <p className="text-gray-600 text-sm">Fraction of the cost of guided tours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      {!token && (
        <section className="bg-slate-900 py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-black uppercase text-white mb-4">Ready to Explore?</h2>
            <p className="text-slate-400 mb-8 text-lg">Start your audio adventure today. No app download required.</p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="bg-cyan-500 hover:bg-cyan-400 text-white px-10 py-4 rounded-full font-bold text-xl shadow-xl hover:shadow-2xl transition-all active:scale-95 uppercase tracking-wide"
            >
              Get Started Free
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} Audiostride. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/blog" className="text-slate-400 hover:text-white text-sm transition-colors">Blog</Link>
            <a href="#tours" className="text-slate-400 hover:text-white text-sm transition-colors">Tours</a>
            <Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link>
          </div>
        </div>
        <p className="text-slate-600 text-xs text-center mt-4 max-w-2xl mx-auto">
          By using Audiostride, you agree to our <Link to="/privacy" className="underline hover:text-slate-400">Privacy Policy</Link> and that your tour activity may be used to improve our experiences.
        </p>
      </footer>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default HomePage;
